import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Button } from "react-bootstrap";
import PermissionChecklist from "./PermissionChecklist";
import {
  getRole,
  isErpApiSuccess,
  listAdminUserPermissions,
  listRoles,
} from "../../erpClient";

function emptyRow(isDefault = false) {
  return {
    key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    branch_id: "",
    role_ids: [],
    permission_codes: [],
    role_default_codes: [],
    is_default: !!isDefault,
  };
}

export function normalizeBranchAccessesFromUser(userObj) {
  if (Array.isArray(userObj?.branch_accesses) && userObj.branch_accesses.length) {
    return userObj.branch_accesses.map((row, idx) => {
      const roleIds = Array.isArray(row.role_ids)
        ? row.role_ids.map(Number).filter((n) => Number.isFinite(n) && n > 0)
        : [];
      const codes = Array.isArray(row.permission_codes)
        ? row.permission_codes.map((c) => String(c).toUpperCase())
        : [];
      const roleDefaults = Array.isArray(row.role_permission_codes)
        ? row.role_permission_codes.map((c) => String(c).toUpperCase())
        : codes;
      return {
        key: `loaded-${row.branch_id ?? idx}`,
        branch_id: row.branch_id != null ? Number(row.branch_id) : "",
        role_ids: roleIds,
        permission_codes: codes,
        role_default_codes: roleDefaults,
        is_default: !!(row.is_default === true || row.is_default === 1),
      };
    });
  }

  // Legacy single-branch shape
  const branchId = Number(
    userObj?.branch_id ?? userObj?.default_branch_id ?? userObj?.branch?.id,
  );
  let roleIds = [];
  if (Array.isArray(userObj?.role_ids)) {
    userObj.role_ids.forEach((entry) => {
      if (entry && typeof entry === "object" && Array.isArray(entry.ids)) {
        entry.ids.forEach((id) => {
          const n = Number(id);
          if (Number.isFinite(n) && n > 0) roleIds.push(n);
        });
      } else {
        const n = Number(entry);
        if (Number.isFinite(n) && n > 0) roleIds.push(n);
      }
    });
  }
  let codes = [];
  if (Array.isArray(userObj?.permission_codes)) {
    codes = userObj.permission_codes.map((c) => String(c).toUpperCase());
  } else if (userObj?.permissions && typeof userObj.permissions === "object") {
    codes = Object.entries(userObj.permissions)
      .filter(([, v]) => v === 1 || v === "1")
      .map(([k]) => String(k).toUpperCase());
  }
  const roleDefaults = Array.isArray(userObj?.role_permission_codes)
    ? userObj.role_permission_codes.map((c) => String(c).toUpperCase())
    : codes;

  if (!Number.isFinite(branchId) || branchId <= 0) {
    return [emptyRow(true)];
  }
  return [
    {
      key: `legacy-${branchId}`,
      branch_id: branchId,
      role_ids: roleIds,
      permission_codes: codes,
      role_default_codes: roleDefaults,
      is_default: true,
    },
  ];
}

/** Strip UI-only fields for API payloads. */
export function toBranchAccessesPayload(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => Number(r.branch_id) > 0)
    .map((r) => ({
      branch_id: Number(r.branch_id),
      role_ids: Array.isArray(r.role_ids)
        ? r.role_ids.map(Number).filter((n) => Number.isFinite(n) && n > 0)
        : [],
      permission_codes: Array.isArray(r.permission_codes)
        ? r.permission_codes.map((c) => String(c).toUpperCase())
        : [],
      is_default: !!r.is_default,
    }));
}

/**
 * Multi-branch access editor: Branch | Role | default | permission ticks.
 */
function BranchAccessEditor({
  value = [],
  onChange,
  branchOptions = [],
  square = false,
  compact = false,
}) {
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesById, setRolesById] = useState({});
  const [catalogCodes, setCatalogCodes] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const [rolesRes, catalogRes] = await Promise.all([
          listRoles(),
          listAdminUserPermissions(),
        ]);
        if (cancelled) return;
        const roles = Array.isArray(rolesRes?.data?.roles)
          ? rolesRes.data.roles
          : [];
        setRoleOptions(roles);
        const byId = {};
        roles.forEach((r) => {
          byId[r.id] = r;
        });
        setRolesById(byId);
        const codes = Array.isArray(catalogRes?.data?.permission_codes)
          ? catalogRes.data.permission_codes.map((c) => String(c).toUpperCase())
          : [];
        setCatalogCodes(codes);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(
    () => (Array.isArray(value) && value.length ? value : [emptyRow(true)]),
    [value],
  );

  const setRows = useCallback(
    (next) => {
      if (typeof onChange === "function") onChange(next);
    },
    [onChange],
  );

  const updateRow = useCallback(
    (key, patch) => {
      setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    },
    [rows, setRows],
  );

  const applyRoleDefaults = useCallback(
    async (key, roleId) => {
      if (!roleId) {
        updateRow(key, {
          role_ids: [],
          permission_codes: [],
          role_default_codes: [],
        });
        return;
      }
      let codes = rolesById[roleId]?.permission_codes;
      if (!Array.isArray(codes)) {
        try {
          const res = await getRole({ id: Number(roleId) });
          if (isErpApiSuccess(res) && res?.data?.role) {
            codes = res.data.role.permission_codes || [];
            setRolesById((prev) => ({ ...prev, [roleId]: res.data.role }));
          } else {
            codes = [];
          }
        } catch {
          codes = [];
        }
      }
      const normalized = (codes || []).map((c) => String(c).toUpperCase());
      updateRow(key, {
        role_ids: [Number(roleId)],
        permission_codes: normalized,
        role_default_codes: normalized,
      });
    },
    [rolesById, updateRow],
  );

  const addRow = () => {
    setRows([...rows, emptyRow(rows.every((r) => !r.is_default))]);
  };

  const removeRow = (key) => {
    if (rows.length <= 1) return;
    const next = rows.filter((r) => r.key !== key);
    if (!next.some((r) => r.is_default) && next.length) {
      next[0] = { ...next[0], is_default: true };
    }
    setRows(next);
  };

  const setDefault = (key) => {
    setRows(rows.map((r) => ({ ...r, is_default: r.key === key })));
  };

  const usedBranchIds = new Set(
    rows.map((r) => Number(r.branch_id)).filter((n) => Number.isFinite(n) && n > 0),
  );

  return (
    <div>
      {loadingMeta && (
        <p className="text-muted small mb-2">Loading roles & permissions…</p>
      )}
      {rows.map((row, idx) => {
        const roleId = row.role_ids?.[0] || "";
        return (
          <div
            key={row.key}
            className={`border rounded p-3 mb-3 ${square ? "rounded-0" : ""}`}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="small">Branch access #{idx + 1}</strong>
              <div className="d-flex gap-2 align-items-center">
                <Form.Check
                  type="radio"
                  name="branch-access-default"
                  id={`default-${row.key}`}
                  label="Default"
                  checked={!!row.is_default}
                  onChange={() => setDefault(row.key)}
                />
                <Button
                  variant="outline-danger"
                  size="sm"
                  disabled={rows.length <= 1}
                  onClick={() => removeRow(row.key)}
                >
                  Remove
                </Button>
              </div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-md-6">
                <Form.Label className="small mb-1">Branch</Form.Label>
                <Form.Select
                  value={row.branch_id || ""}
                  className={square ? "rounded-0" : ""}
                  onChange={(e) => {
                    const bid = e.target.value ? Number(e.target.value) : "";
                    updateRow(row.key, { branch_id: bid });
                  }}
                >
                  <option value="">Select branch</option>
                  {branchOptions.map((b) => {
                    const id = Number(b.value ?? b.id);
                    const taken =
                      usedBranchIds.has(id) && Number(row.branch_id) !== id;
                    return (
                      <option key={id} value={id} disabled={taken}>
                        {b.label ?? b.name}
                        {taken ? " (already added)" : ""}
                      </option>
                    );
                  })}
                </Form.Select>
              </div>
              <div className="col-md-6">
                <Form.Label className="small mb-1">Role</Form.Label>
                <Form.Select
                  value={roleId}
                  className={square ? "rounded-0" : ""}
                  onChange={(e) => {
                    const rid = e.target.value ? Number(e.target.value) : null;
                    void applyRoleDefaults(row.key, rid);
                  }}
                >
                  <option value="">Select role</option>
                  {roleOptions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted small">
                Role change auto-selects permissions; customize below.
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={!roleId}
                onClick={() => applyRoleDefaults(row.key, Number(roleId))}
              >
                Reset to role defaults
              </Button>
            </div>
            <PermissionChecklist
              value={row.permission_codes}
              onChange={(codes) =>
                updateRow(row.key, { permission_codes: codes })
              }
              roleDefaultCodes={row.role_default_codes}
              catalogCodes={catalogCodes}
              showRoleHint
              square={square}
              compact={compact}
            />
          </div>
        );
      })}
      <Button
        variant="outline-primary"
        size="sm"
        className="mb-2"
        onClick={addRow}
        disabled={
          branchOptions.length > 0 && usedBranchIds.size >= branchOptions.length
        }
      >
        Add branch
      </Button>
    </div>
  );
}

export default BranchAccessEditor;
